import "./style.scss";
import { MutableRefObject, PropsWithChildren, ReactElement, useId } from "react";
import { CheckboxPropsType } from "../Checkbox";
import Icon from "@able/react/dist/Icon";
import { AbleCommonProps } from "../Types";

export type CheckboxChildType = Omit<CheckboxPropsType, "id" | "checked" | "onChange" | "name" | "errorText" | "variant" | "developmentUrl"> & { id: string, value: string };

export type CheckboxGroupType = {
    /** Selectable Input Group label */
    groupLabel: string;
    /** Is field required? */
    required?: boolean;
    /** Additional CSS classes */
    className?: string;
    /** Error message to display - default 'Selection required.' */
    errorText?: string | ReactElement<HTMLSpanElement>;
    /** Help text to display */
    helpText?: string;
    id?: string;
    /** Shows error message */
    error?: boolean;
    /** Variant  */
    variant?: "Compact" | "Comfortable";
    /** Ref for <legend> element  */
    legendRef?: MutableRefObject<null>;
    /** ID for <legend> element  */
    legendId?: string;
    /** ID for group aria-describedby - can only be used when groupLabel is empty */
    describedById?: string;
    /** ID for group aria-labelledby - can only be used when groupLabel is empty */
    labelById?: string;
    /** value for group aria-label - can only be used when groupLabel is empty */
    ariaLabel?: string;
} & PropsWithChildren & AbleCommonProps;

const CheckboxGroup = (props: CheckboxGroupType) => {
    const {
        groupLabel,
        variant = "Compact",
        helpText,
        error,
        errorText,
        developmentUrl,
        required = true,
        className = "",
        describedById,
        labelById,
        id,
        legendId,
        legendRef,
        ariaLabel,
        children
    } = props;
    const errorId = `${useId()}--error-id`;

    const getAriaDescribedBy = () => {
        if (!groupLabel && describedById) {
            return {
                "aria-describedby": describedById,
            };
        }
        return {};
    };
    const getAriaLabel = () => {
        const label: { [key: string]: string } = {}
        if (!groupLabel) {
            if (labelById) {
                label["aria-labelledby"] = labelById
            }
            if (ariaLabel) {
                label["aria-label"] = ariaLabel
            }

        }
        return label;
    };

    const errorWarning = () => {
        return groupLabel || labelById || ariaLabel
            ? '' : console.error(
                "You must include either a groupLabel or labelById or ariaLabel prop to ensure correct titling of this group for accessibility"
            )
    }

    return (
        <div className={`ted-ui-checkbox-group ${className} ${variant}`} id={id}>
            <fieldset {...getAriaDescribedBy()} {...getAriaLabel()} key={id}>
                {groupLabel ? <legend
                    ref={legendRef}
                    id={legendId}
                    tabIndex={legendId || (legendRef && legendRef.current) ? -1 : undefined}
                >
                    <>
                        {groupLabel} {required ? "" : "(optional)"}
                        {helpText ? (
                            <span className="ted-ui-checkbox-group-helper">{helpText}</span>
                        ) : (
                            ""
                        )}
                        {error ? (
                            <span id={errorId} className={`ted-ui-checkbox-group__error`}>
                                <Icon
                                    icon="Error"
                                    screenReaderContent="Error"
                                    developmentUrl={developmentUrl}
                                />
                                {errorText ? errorText : "Selection required."}
                            </span>
                        ) : (
                            ""
                        )}
                    </>
                </legend> : null}
                <>{errorWarning()}</>
                {children}
            </fieldset>
        </div>
    );
};

export default CheckboxGroup;
